import { 
  startOfDay, 
  addDays, 
  subDays, 
  startOfWeek, 
  endOfWeek, 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear, 
  subMonths, 
  subYears,
  format
} from 'date-fns';
import { toZonedTime, fromZonedTime } from 'date-fns-tz';

export type PayrollDatePreset = 
  | 'today' 
  | 'yesterday' 
  | 'wtd' 
  | 'last_week' 
  | 'mtd' 
  | 'last_month' 
  | 'ytd' 
  | 'last_year' 
  | 'custom';

export interface PayrollDateRange {
  startDate: Date;  // Inclusive: start of first day
  endDate: Date;    // EXCLUSIVE: start of day AFTER last day
  label: string;
}

export const PAYROLL_PRESET_OPTIONS: { value: PayrollDatePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'wtd', label: 'Week to Date (Mon–Today)' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'mtd', label: 'Month to Date' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

/**
 * Gets the payroll date range for a given preset.
 * All dates use the shop's timezone for accurate day boundaries.
 * endDate is ALWAYS EXCLUSIVE (start of the day after the last day).
 */
export function getPayrollDateRange(
  preset: PayrollDatePreset, 
  timezone: string = 'America/Chicago',
  customStart?: Date,
  customEnd?: Date
): PayrollDateRange {
  // Get "now" in the shop's timezone
  const nowUtc = new Date();
  const nowInTz = toZonedTime(nowUtc, timezone);
  
  let startDate: Date;
  let endDate: Date;
  let label: string;

  switch (preset) {
    case 'today':
      startDate = startOfDay(nowInTz);
      endDate = addDays(startOfDay(nowInTz), 1); // Tomorrow 00:00 (exclusive)
      label = 'Today';
      break;
      
    case 'yesterday':
      startDate = startOfDay(subDays(nowInTz, 1));
      endDate = startOfDay(nowInTz); // Today 00:00 (exclusive)
      label = 'Yesterday';
      break;
      
    case 'wtd':
      // Week starts Monday (ISO standard)
      startDate = startOfWeek(nowInTz, { weekStartsOn: 1 });
      endDate = addDays(startOfDay(nowInTz), 1); // Tomorrow 00:00 (exclusive)
      label = 'Week to Date (Mon–Today)';
      break;
      
    case 'last_week':
      // Previous week Monday–Sunday
      startDate = subDays(startOfWeek(nowInTz, { weekStartsOn: 1 }), 7);
      endDate = startOfWeek(nowInTz, { weekStartsOn: 1 }); // This Monday 00:00 (exclusive)
      label = 'Last Week';
      break;
      
    case 'mtd':
      startDate = startOfMonth(nowInTz);
      endDate = addDays(startOfDay(nowInTz), 1); // Tomorrow 00:00 (exclusive)
      label = 'Month to Date';
      break;
      
    case 'last_month':
      startDate = startOfMonth(subMonths(nowInTz, 1));
      endDate = startOfMonth(nowInTz); // This month 1st 00:00 (exclusive)
      label = 'Last Month';
      break;
      
    case 'ytd':
      startDate = startOfYear(nowInTz);
      endDate = addDays(startOfDay(nowInTz), 1); // Tomorrow 00:00 (exclusive)
      label = 'Year to Date';
      break;
      
    case 'last_year':
      startDate = startOfYear(subYears(nowInTz, 1));
      endDate = startOfYear(nowInTz); // This year Jan 1 00:00 (exclusive)
      label = 'Last Year';
      break;
      
    case 'custom':
      if (customStart && customEnd) {
        // IV-D-22 R74 FIX-1: custom dates come from the browser date picker
        // (wall-clock in the user's browser TZ). The previous flow passed the
        // Date objects through `startOfDay` (operates in browser TZ) and then
        // applied `fromZonedTime(..., timezone)` at the end, which treats the
        // date's computer-local parts AS IF they were shop-TZ parts. When the
        // user's browser TZ differs from the shop TZ, this shifts the day
        // boundary by the TZ offset, so a custom payroll range picks up a
        // different calendar day than the user selected. Extract the calendar
        // date the user actually picked (via `format(..., 'yyyy-MM-dd')`),
        // interpret it as shop-TZ midnight using the string-overload of
        // `fromZonedTime` (the pattern used across the rest of the codebase),
        // and early-return to bypass the end-of-function double-conversion.
        const startDateStr = format(customStart, 'yyyy-MM-dd');
        const endDateStr = format(customEnd, 'yyyy-MM-dd');
        const startUtc = fromZonedTime(`${startDateStr}T00:00:00`, timezone);
        const endUtc = new Date(
          fromZonedTime(`${endDateStr}T00:00:00`, timezone).getTime() + 24 * 60 * 60 * 1000
        );
        return {
          startDate: startUtc,
          endDate: endUtc,
          label: `${format(customStart, 'MMM d')} – ${format(customEnd, 'MMM d, yyyy')}`,
        };
      }
      // Fallback to today
      startDate = startOfDay(nowInTz);
      endDate = addDays(startOfDay(nowInTz), 1);
      label = 'Custom Range';
      break;
      
    default:
      startDate = startOfDay(nowInTz);
      endDate = addDays(startOfDay(nowInTz), 1);
      label = 'Today';
  }

  // Convert back to UTC for database queries
  return {
    startDate: fromZonedTime(startDate, timezone),
    endDate: fromZonedTime(endDate, timezone),
    label,
  };
}

/**
 * Gets the comparison range (same period shifted back 1 year)
 */
export function getComparisonRange(range: PayrollDateRange): PayrollDateRange {
  return {
    startDate: subYears(range.startDate, 1),
    endDate: subYears(range.endDate, 1),
    label: `${range.label} (Last Year)`,
  };
}

/**
 * Formats minutes as hours with 2 decimal places
 */
export function formatMinutesAsHours(minutes: number): string {
  return (minutes / 60).toFixed(2);
}

/**
 * Calculates efficiency percentage
 * Returns null if clock minutes is 0
 */
export function calculateEfficiency(producedMinutes: number, clockMinutes: number): number | null {
  if (clockMinutes <= 0) return null;
  return Math.round((producedMinutes / clockMinutes) * 100);
}

/**
 * Gets color class for efficiency percentage
 */
export function getEfficiencyColor(efficiency: number | null): string {
  if (efficiency === null) return 'text-muted-foreground';
  if (efficiency >= 100) return 'text-green-600 dark:text-green-400';
  if (efficiency >= 80) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-red-600 dark:text-red-400';
}
